import "./AuthoringFields.scss";

import clsx from "clsx";
import type { FC, ReactNode } from "react";
import { Doc } from "sharedb/lib/client";

import {
  ChoiceFieldChoiceType,
  CodeCellTypeOption,
  CodeFieldType,
  PathType,
  WorksheetType,
} from "../../../shared/types/worksheet";
import { CODE_CELLTYPE_OPTIONS, getFieldShortId } from "../../../shared/worksheets/constants";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  AddPlusIcon,
  ArrowUpIcon,
  EyeNotVisibleIcon,
  EyeVisibleIcon,
  LockUnlockedIcon,
  MultipleChoiceMultipleIcon,
  MultipleChoiceSingleIcon,
  TrashDeleteIcon,
} from "mds/icons";

interface MultipleSwitchDropdownProps {
  doc: Doc<WorksheetType>;
  path: PathType;
  t: "ChoiceField" | "FileField";
  m: boolean;
  id: string;
}

const CHOICE_FIELD_MULTIPLE_OPTIONS = [
  { value: true, labelKey: "multiple_answers", icon: MultipleChoiceMultipleIcon },
  { value: false, labelKey: "single_answer", icon: MultipleChoiceSingleIcon },
] as const;

const FILE_FIELD_MULTIPLE_OPTIONS = [
  { value: true, labelKey: "multiple_files", icon: ArrowUpIcon },
  { value: false, labelKey: "single_file", icon: ArrowUpIcon },
] as const;

const CODE_FIELD_ICONS: Record<CodeCellTypeOption, ReactNode> = {
  editable: <LockUnlockedIcon />,
  readonly: <EyeVisibleIcon />,
  hidden: <EyeNotVisibleIcon />,
};

// TODO: This component is only used in the block authoring toolbar now, and should maybe
// be moved to a common location.
export const MultipleSwitchDropdown: FC<MultipleSwitchDropdownProps> = ({
  doc,
  path,
  t: type,
  m: multiple,
  id,
}) => {
  const options =
    type === "ChoiceField" ? CHOICE_FIELD_MULTIPLE_OPTIONS : FILE_FIELD_MULTIPLE_OPTIONS;
  const titleKey = type === "ChoiceField" ? "mc_title" : "file_title";

  const onChange = (newValue: boolean) => {
    if (newValue !== multiple) {
      doc.submitOp([
        {
          p: [...path, "m"],
          oi: newValue,
          od: multiple,
        },
      ]);
    }
  };

  const currentOption = options.find((o) => o.value === multiple);
  const Icon = currentOption?.icon;

  return (
    <Menu
      direction="right"
      menuButton={
        <SquareIconButton
          className="gap-2"
          kind="tertiary"
          size="s"
          title={t("tooltip.answer_type")}
        >
          {Icon ? <Icon /> : null}
        </SquareIconButton>
      }
    >
      <MenuItem key="title" kind="instructional">
        {t(`authoring_fields.${titleKey}`)}
      </MenuItem>

      {options.map(({ labelKey, value, icon: IconComponent }) => {
        const key = `${id}-multiple-option-${value}`;
        return (
          <MenuItem
            className={clsx(value === multiple && "selected")}
            key={key}
            onClick={() => onChange(value as boolean)}
          >
            <IconComponent className="mx-2" />{" "}
            {t(
              `authoring_fields.${
                labelKey as "multiple_answers" | "single_answer" | "multiple_files" | "single_file"
              }`,
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
};

interface ChoicesAuthoringFieldProps {
  doc: Doc<WorksheetType>;
  path: PathType;
  id: string;
  c: ChoiceFieldChoiceType[];
  m: boolean;
}

export const ChoicesAuthoringField: FC<ChoicesAuthoringFieldProps> = ({
  doc,
  path,
  c: choices,
  id,
  m: multiple,
}) => {
  // Note that currently, the "value" of a choice is just the index of the choice as string
  // TODO: We should change this component directly act on the index, not the "value" and other
  // code to not use the value.

  // TODO: Editing of these text fields is very inefficient, because we're replacing the content every time.

  const onChange = (targetChoice: ChoiceFieldChoiceType, newLabel: string) => {
    doc.submitOp([
      {
        p: [...path, "c", choices.indexOf(targetChoice), "l"],
        oi: newLabel,
        od: targetChoice.l,
      },
    ]);
  };

  const onRemove = (targetAnswer: string) => {
    doc.submitOp([
      {
        p: [...path, "c", choices.findIndex((c) => c.v === targetAnswer)],
        ld: choices.find((c) => c.v === targetAnswer),
      },
    ]);
  };

  const onAdd = () => {
    // In ShareDB, if we specify a position past any existing positions,
    // it just appends to the end of the list
    const INSERT_BEFORE_LARGE_INDEX = 999;
    doc.submitOp([
      {
        p: [...path, "c", INSERT_BEFORE_LARGE_INDEX],
        li: {
          v: String(choices.length),
          l: "",
          id: getFieldShortId(),
        },
      },
    ]);
  };

  return (
    <div className="mt-4">
      <div>
        {choices.map((choice) => {
          const key = `${id}-answers-${choice.id}`;
          const type = multiple ? "checkbox" : "radio";

          return (
            <div className="mc-authoring flex items-center" key={key}>
              <input className="mc-authoring-radio mr-2" type={type} disabled />

              <input
                className="mc-authoring-input w-full"
                id={key}
                name={key}
                placeholder={t("worksheet.placeholder.multiple_choice")}
                type="text"
                value={choice.l}
                onChange={(e) => onChange(choice, e.target.value)}
              />

              <SquareIconButton
                className="ml-2"
                eventName="Content block - Delete"
                eventProps={{ blockType: "MultipleChoice" }}
                kind="caution"
                size="s"
                title={t("tooltip.delete_answer")}
                onClick={() => onRemove(choice.v)}
              >
                <TrashDeleteIcon />
              </SquareIconButton>
            </div>
          );
        })}

        <SquareButton
          className="mt-2"
          eventName="MC field - Add new answer"
          kind="ghost"
          size="s"
          onClick={() => onAdd()}
        >
          <IconText iconStart={<AddPlusIcon />} text={t("authoring_fields.add_answer")} />
        </SquareButton>
      </div>
    </div>
  );
};

interface CodeCellTypeSwitchDropdownProps {
  doc: Doc<WorksheetType>;
  path: PathType;
  cellType: CodeFieldType["cellType"];
  id: string;
}

export const CodeCellTypeSwitchDropdown: FC<CodeCellTypeSwitchDropdownProps> = ({
  doc,
  path,
  cellType,
  id,
}) => {
  const onChange = (newCellType: string) => {
    if (newCellType !== cellType) {
      doc.submitOp([
        {
          p: [...path, "cellType"],
          oi: newCellType,
          od: cellType,
        },
      ]);
    }
  };

  return (
    <Menu
      direction="right"
      menuButton={
        <SquareButton
          eventName="Code block - Settings"
          kind="tertiary"
          size="s"
          title={t("tooltip.code_block_settings")}
        >
          <LockUnlockedIcon />
        </SquareButton>
      }
    >
      <MenuItem key="title" kind="instructional">
        {t("authoring_fields.code_title")}
      </MenuItem>

      {CODE_CELLTYPE_OPTIONS.map(({ value }) => {
        const key = `${id}-code-option-${value}`;
        return (
          <MenuItem
            className={clsx(value === cellType && "selected")}
            eventName={`Code block - Settings / ${value}`}
            key={key}
            onClick={() => onChange(value)}
          >
            {CODE_FIELD_ICONS[value]}
            {t(`authoring_fields.${value}`)}
          </MenuItem>
        );
      })}
    </Menu>
  );
};
